import React, { useState, useEffect, useRef } from 'react';
import { Settings, Minus, Plus } from 'lucide-react';
import { Select, SelectOption } from '../../base/inputs/select';
import {useSessionState} from "../session/SessionStateContext";
import {useOutputConfig} from "./OutputConfigContext";
import {useZoom} from "../session/ZoomContext";

async function listAudioOutputDevices(): Promise<Array<MediaDeviceInfo & {
    default: boolean;
}>> {
    const devices = await navigator.mediaDevices.enumerateDevices() as Array<MediaDeviceInfo & {
        default: boolean;
    }>;

    const audioDevices = devices.filter(
        (device) => device.kind === 'audiooutput',
    );

    const defaultDeviceIndex = audioDevices.findIndex(
        (device) => device.deviceId === 'default',
    );

    const deviceList = [];

    if (defaultDeviceIndex !== -1) {
        let defaultDevice = audioDevices.splice(defaultDeviceIndex, 1)[0];

        const existingIndex = audioDevices.findIndex(
            (device) => device.groupId === defaultDevice.groupId,
        );

        if (existingIndex !== -1) {
            defaultDevice = audioDevices.splice(existingIndex, 1)[0];
        }

        defaultDevice.default = true;
        deviceList.push(defaultDevice);
    }

    return deviceList.concat(audioDevices);
}

export function SettingsButton() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [outputConfig, setOutputConfig] = useOutputConfig();
    const [zoom, setZoom] = useZoom();
    const sessionState = useSessionState();
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [menuPosition, setMenuPosition] = useState<'right' | 'left'>('right');

    const toggleMenu = async () => {
        setIsMenuOpen(!isMenuOpen);

        if (!isMenuOpen) {
            const audioOutputDevices = await listAudioOutputDevices();
            const defaultDevice = audioOutputDevices.find(device => device.default);

            setDevices(audioOutputDevices);

            if (defaultDevice && !outputConfig.device) {
                setOutputConfig({ ...outputConfig, device: defaultDevice });
            }

            if (buttonRef.current) {
                const buttonRect = buttonRef.current.getBoundingClientRect();
                setMenuPosition(buttonRect.left < 200 ? 'left' : 'right');
            }
        }
    };

    const handleDeviceSelect = (option: SelectOption) => {
        const deviceId = option.value;
        const selectedDevice = devices.find(device => device.deviceId === deviceId);

        if (selectedDevice) {
            setOutputConfig({ ...outputConfig, device: selectedDevice });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative" ref={menuRef}>
            <button
                ref={buttonRef}
                className="p-2 rounded-full hover:bg-gray-200"
                onClick={toggleMenu}
            >
                <Settings className="h-5 w-5 text-gray-600" />
            </button>
            {isMenuOpen && (
                <div
                    className={`absolute mt-2 w-64 bg-white border border-gray-300 rounded shadow-lg z-10 ${
                        menuPosition === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    <div className="p-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Output Device
                        </label>
                        <Select
                            options={devices.map(device => ({
                                value: device.deviceId,
                                label: device.label || 'Unknown Device',
                            }))}
                            defaultValue={outputConfig.device?.deviceId}
                            onChange={handleDeviceSelect}
                            placeholder="Default device is used"
                            disabled={sessionState.isSessionRunning}
                        />
                    </div>
                    <div className="p-2 border-t border-gray-300">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Stereo
                        </label>
                        <div className="flex flex-col items-center">
                            <div className="flex justify-between w-full text-sm text-gray-700 mb-2">
                                <span>Left</span>
                                <span>Both</span>
                                <span>Right</span>
                            </div>
                            <input
                                type="range"
                                min="-1"
                                max="1"
                                step="1"
                                value={outputConfig.stereo}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);

                                    setOutputConfig({
                                        ...outputConfig,
                                        stereo: value,
                                    });
                                }}
                                className="w-full appearance-none h-2 bg-gray-200 rounded cursor-pointer disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none
                                [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:disabled:bg-gray-400 [&:hover::-webkit-slider-thumb]:disabled:bg-gray-400 [&::-webkit-slider-thumb]:bg-indigo-600 [&:hover::-webkit-slider-thumb]:bg-indigo-700 [&::-webkit-slider-thumb]:rounded-full
                                [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:disabled:bg-gray-400 [&:hover::-moz-range-thumb]:disabled:bg-gray-400 [&::-moz-range-thumb]:bg-indigo-600 [&:hover::-moz-range-thumb]:bg-indigo-700 [&::-moz-range-thumb]:rounded-full"
                                disabled={sessionState.isSessionRunning}
                            />
                        </div>
                    </div>
                    <div className="p-2 border-t border-gray-300">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Zoom
                        </label>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setZoom(Math.max(50, zoom - 10))}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed self-start mt-0.5"
                                disabled={zoom <= 50}
                                aria-label="Decrease zoom"
                            >
                                <Minus className="h-4 w-4 text-gray-600" />
                            </button>
                            <div className="flex-1">
                                <input
                                    type="range"
                                    min="50"
                                    max="200"
                                    step="10"
                                    value={zoom}
                                    onChange={(e) => setZoom(parseInt(e.target.value, 10))}
                                    className="w-full appearance-none h-2 bg-gray-200 rounded cursor-pointer [&::-webkit-slider-thumb]:appearance-none
                                    [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:bg-indigo-600 [&:hover::-webkit-slider-thumb]:bg-indigo-700 [&::-webkit-slider-thumb]:rounded-full
                                    [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:bg-indigo-600 [&:hover::-moz-range-thumb]:bg-indigo-700 [&::-moz-range-thumb]:rounded-full"
                                />
                                <div className="flex justify-center mt-1">
                                    <span className="text-xs text-gray-600">{zoom}%</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setZoom(Math.min(200, zoom + 10))}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed self-start mt-0.5"
                                disabled={zoom >= 200}
                                aria-label="Increase zoom"
                            >
                                <Plus className="h-4 w-4 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
